import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeclarationPreview = ({ declarationData, onDownload, viewMode = false }) => {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleZoomIn = () => {
    if (zoomLevel < 150) setZoomLevel(prev => prev + 25);
  };

  const handleZoomOut = () => {
    if (zoomLevel > 50) setZoomLevel(prev => prev - 25);
  };

  return (
    <div className="card flex flex-col" style={{ height: '600px', maxHeight: '600px' }}>
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-border flex-shrink-0">
        <h3 className="text-lg font-semibold text-foreground">Declaration Preview</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ZoomOut"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 50}
            aria-label="Zoom out"
          />
          <span className="text-sm font-medium text-muted-foreground min-w-[60px] text-center">
            {zoomLevel}%
          </span>
          <Button
            variant="outline"
            size="sm"
            iconName="ZoomIn"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 150}
            aria-label="Zoom in"
          />
          {!viewMode && (
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={onDownload}
            >
              Download
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-muted/30 rounded-lg p-2 sm:p-4 min-h-0">
        <div 
          className="declaration-preview-content bg-white mx-auto shadow-lg transition-all duration-200"
          style={{ 
            width: `${zoomLevel}%`,
            minWidth: isMobile ? '100%' : '600px'
          }}
        >
          <div className="p-4 sm:p-6 lg:p-8 xl:p-12">
            {/* Header */}
            <div className="text-center mb-4 sm:mb-6 lg:mb-8 pb-4 sm:pb-6 border-b-2 border-primary">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mb-3 sm:mb-4">
                <Icon name="Trophy" size={32} className="sm:w-12 sm:h-12" color="var(--color-primary)" />
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">Mumbai Indians</h1>
                  <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">Junior Cricket Registration</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">Registration Declaration Form</p>
            </div>

            {/* School Details */}
            <div className="mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-3 sm:mb-4 flex items-center gap-2">
                <Icon name="School" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
                School Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <p className="text-muted-foreground">School Name:</p>
                  <p className="font-medium text-foreground">{declarationData?.schoolName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">School Email:</p>
                  <p className="font-medium text-foreground">{declarationData?.schoolEmail}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Age Category:</p>
                  <p className="font-medium text-foreground">{declarationData?.ageCategory}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Registration Date:</p>
                  <p className="font-medium text-foreground">{declarationData?.registrationDate}</p>
                </div>
              </div>
            </div>

            {/* Coach Details */}
            <div className="mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-3 sm:mb-4 flex items-center gap-2">
                <Icon name="UserCheck" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
                Coach Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <p className="text-muted-foreground">Primary Coach:</p>
                  <p className="font-medium text-foreground">{declarationData?.primaryCoach?.name}</p>
                  <p className="text-xs text-muted-foreground">{declarationData?.primaryCoach?.mobile}</p>
                </div>
                {declarationData?.secondaryCoach && (
                  <div>
                    <p className="text-muted-foreground">Secondary Coach:</p>
                    <p className="font-medium text-foreground">{declarationData?.secondaryCoach?.name}</p>
                    <p className="text-xs text-muted-foreground">{declarationData?.secondaryCoach?.mobile}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Players List */}
            <div className="mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-3 sm:mb-4 flex items-center gap-2">
                <Icon name="Users" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
                Team Roster ({declarationData?.players?.length} Players)
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm border border-border" style={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '35%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '27%' }} />
                  </colgroup>
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-2 sm:px-3 py-2 text-left font-semibold text-foreground border-b border-border">S.No</th>
                      <th className="px-2 sm:px-3 py-2 text-left font-semibold text-foreground border-b border-border">Player Name</th>
                      <th className="px-2 sm:px-3 py-2 text-left font-semibold text-foreground border-b border-border">DOB</th>
                      <th className="px-2 sm:px-3 py-2 text-left font-semibold text-foreground border-b border-border">Role</th>
                      <th className="px-2 sm:px-3 py-2 text-left font-semibold text-foreground border-b border-border">MCA ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {declarationData?.players?.map((player, index) => (
                      <tr key={player?.id} className={index % 2 === 0 ? 'bg-white' : 'bg-muted/30'}>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 border-b border-border">{index + 1}</td>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 border-b border-border font-medium break-words">
                          <span className="block">{player?.name}</span>
                          {player?.isCaptain && (
                            <span className="inline-block mt-1 text-xs bg-primary/10 text-primary px-1.5 sm:px-2 py-0.5 rounded">Captain</span>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 border-b border-border break-words">{player?.dob}</td>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 border-b border-border break-words">{player?.role}</td>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 border-b border-border break-words">{player?.mcaId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Declaration Text */}
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-muted/30 rounded-lg">
              <h2 className="text-base sm:text-lg font-semibold text-foreground mb-2 sm:mb-3">Declaration</h2>
              <div className="text-xs sm:text-sm text-foreground space-y-1.5 sm:space-y-2 leading-relaxed">
                <p>I, the undersigned, hereby declare that:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>All information provided in this registration is true and accurate to the best of my knowledge.</li>
                  <li>All players listed are bonafide students of the mentioned school and meet the age eligibility criteria.</li>
                  <li>All uploaded documents (Aadhar Card, Birth Certificate, School ID, Bonafide Certificate) are genuine and unaltered.</li>
                  <li>Players have valid MCA registration and are eligible to participate in the tournament.</li>
                  <li>I have obtained necessary permissions from parents/guardians for all participating players.</li>
                  <li>The school administration is aware of and approves this team registration.</li>
                  <li>I understand that any false information may lead to disqualification of the entire team.</li>
                </ul>
              </div>
            </div>

            {/* Signature Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-4 sm:mt-6 lg:mt-8 pt-4 sm:pt-6 border-t border-border">
              <div>
                <div className="border-t-2 border-foreground pt-2 mt-6 sm:mt-12">
                  <p className="text-xs sm:text-sm font-medium text-foreground">Coach Signature</p>
                  <p className="text-xs text-muted-foreground mt-1">Date: {declarationData?.registrationDate}</p>
                </div>
              </div>
              <div>
                <div className="border-t-2 border-foreground pt-2 mt-6 sm:mt-12">
                  <p className="text-xs sm:text-sm font-medium text-foreground">School Principal Signature & Stamp</p>
                  <p className="text-xs text-muted-foreground mt-1">Date: _______________</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 sm:mt-6 lg:mt-8 pt-3 sm:pt-4 border-t border-border text-center text-xs text-muted-foreground">
              <p>Mumbai Indians Junior Cricket Registration - {new Date()?.getFullYear()}</p>
              <p className="mt-1 break-words">For queries, contact: mijunior@mumbaiindians.com | +91 98765 43210</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeclarationPreview;